import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBackend } from "../lib/backend";
import type {
  PsychSession,
  PsychSetupStatus,
  SessionEndReason,
  WearableConnection,
} from "../lib/services";

function isSetupError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const next = error as { code?: string; status?: number };
  return next.code === "setup_required" || next.status === 409;
}

function emitSetupRequired(source: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("fintheon:psychassist-setup-required", {
      detail: { source },
    }),
  );
}

export function usePsychAssistSession() {
  const backend = useBackend();
  const [setup, setSetup] = useState<PsychSetupStatus | null>(null);
  const [session, setSession] = useState<PsychSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const retryDeadlineRef = useRef<number | null>(null);
  const sessionRef = useRef<PsychSession | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const refreshSetup = useCallback(async () => {
    const status = await backend.psych.getSetupStatus();
    setSetup(status);
    return status;
  }, [backend]);

  const primaryConnection = useMemo<WearableConnection | null>(() => {
    return setup?.connections.find((item) => item.status !== "revoked") ?? null;
  }, [setup]);

  const syncConnection = useCallback(
    async (connection?: WearableConnection | null) => {
      if (!connection) return null;
      setSyncing(true);
      try {
        const result = await backend.psych.syncWearable(connection.id);
        await refreshSetup();
        setError(null);
        retryDeadlineRef.current = null;
        setRetrySeconds(0);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Wearable sync failed");
        return null;
      } finally {
        setSyncing(false);
      }
    },
    [backend, refreshSetup],
  );

  const finalize = useCallback(
    async (reason: SessionEndReason = "user_stopped") => {
      const active = sessionRef.current;
      if (!active) return null;
      setLoading(true);
      try {
        const next = await backend.psych.finalizeSession(
          active.sessionId,
          reason,
        );
        setSession(next);
        retryDeadlineRef.current = null;
        setRetrySeconds(0);
        return next;
      } finally {
        setLoading(false);
      }
    },
    [backend],
  );

  const start = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await refreshSetup();
      if (status.requiresSetup) {
        emitSetupRequired("psychassist-start");
        return null;
      }
      const connection = status.connections.find(
        (item) => item.status === "connected",
      );
      if (connection) await syncConnection(connection);
      const next = await backend.psych.startSession();
      setSession(next);
      return next;
    } catch (err) {
      if (isSetupError(err)) emitSetupRequired("psychassist-start");
      setError(err instanceof Error ? err.message : "Could not start session");
      return null;
    } finally {
      setLoading(false);
    }
  }, [backend, refreshSetup, syncConnection]);

  useEffect(() => {
    void refreshSetup().catch(() => undefined);
  }, [refreshSetup]);

  useEffect(() => {
    if (!session || session.isFinalized || !primaryConnection) return;
    const interval = window.setInterval(() => {
      void syncConnection(primaryConnection).then((result) => {
        if (result?.sample) return;
        if (!retryDeadlineRef.current) {
          retryDeadlineRef.current = Date.now() + 30_000;
          setRetrySeconds(30);
        }
      });
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [primaryConnection, session, syncConnection]);

  useEffect(() => {
    if (!retrySeconds) return;
    const interval = window.setInterval(() => {
      const deadline = retryDeadlineRef.current;
      if (!deadline) {
        setRetrySeconds(0);
        return;
      }
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRetrySeconds(remaining);
      if (remaining === 0) void finalize("disconnect_timeout");
    }, 1000);
    return () => window.clearInterval(interval);
  }, [finalize, retrySeconds]);

  const currentScore = session?.opening.score ?? null;

  return {
    setup,
    session,
    isActive: Boolean(session && !session.isFinalized),
    loading,
    syncing,
    error,
    retrySeconds,
    currentScore,
    currentLevel: session?.opening.level ?? "unknown",
    recommendation: session?.opening.recommendation ?? null,
    primaryConnection,
    refreshSetup,
    syncConnection,
    start,
    finalize,
  };
}
